import { useCallback, useEffect, useRef, useState } from 'react';

interface UseControllableStateProps<T> {
  prop?: T;
  defaultProp?: T;
  onChange?: (state: T) => void;
}

export function useControllableState<T>({
  prop,
  defaultProp,
  onChange,
}: UseControllableStateProps<T>) {
  const [uncontrolledProp, setUncontrolledProp] = useState(defaultProp);
  const controlled = prop !== undefined;
  const value = controlled ? prop : uncontrolledProp;

  const prevValueRef = useRef(value);
  useEffect(() => {
    if (prevValueRef.current !== value) {
      onChange?.(value as T);
      prevValueRef.current = value as T;
    }
  }, [value, onChange]);

  const setValue = useCallback(
    (nextValue: T) => {
      if (controlled) {
        onChange?.(nextValue);
      } else {
        setUncontrolledProp(nextValue);
        onChange?.(nextValue);
      }
    },
    [controlled, onChange],
  );

  return [value, setValue] as const;
}
